import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList, ViewChildren } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ToastrHelper } from '../../../../helpers/toastr.helper';
import { CommunityFilter } from '../../../../models/identity/community-filter.model';
import { CommunityWithRoles } from '../../../../models/identity/community-with-roles.model';
import { Filters } from '../../../../models/identity/filters.model';
import { User } from '../../../../models/identity/user.model';
import { Interest } from '../../../../models/skills/interest.model';
import { Skill } from '../../../../models/skills/skill.model';
import { AccountsService } from '../../../../services/identity/accounts.service';
import { ProfileService } from '../../../../services/identity/profile.service';
import { WidgetConnectFiltersComponent } from './widget-connect-filters/widget-connect-filters.component';

@Component({
    selector: 'app-widget-connect-modal',
    templateUrl: './widget-connect-modal.component.html',
    styleUrls: ['./widget-connect-modal.component.scss'],
})
export class WidgetConnectModalComponent implements OnInit, OnDestroy {
    @ViewChildren(WidgetConnectFiltersComponent) filtersComponents: QueryList<WidgetConnectFiltersComponent>;
    @Input() visible: boolean = false;
    @Output() visibleChange = new EventEmitter<boolean>();
    @Output() searchSubmit = new EventEmitter<Filters | null>();
    @Input() user: User;
    public skills: Skill[] = [];
    public communities: CommunityFilter[];
    public selectedCommunity: CommunityWithRoles;
    public interests: Interest[] = [];
    public activeTabIndex: number = 0;
    protected destroy$ = new Subject<void>();

    constructor(
        private readonly profileService: ProfileService,
        private readonly toastrHelper: ToastrHelper,
        private readonly accountsService: AccountsService,
    ) {}
    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    ngOnInit(): void {
        this.loadUserProfile();
    }

    closeModal() {
        this.visible = false;
        this.visibleChange.emit(this.visible);
    }

    onHideModal() {
        this.visibleChange.emit(false);
    }

    onTabChange(event: any) {
        this.activeTabIndex = event.index;
        if (this.activeTabIndex === 0) {
            const interestFilter = this.filtersComponents?.find((c) => c.radioButtonGroupName === 'interestGroup');
            if (interestFilter) interestFilter.reset();
        } else if (this.activeTabIndex === 1) {
            const skillFilter = this.filtersComponents?.find((c) => c.radioButtonGroupName === 'skillGroup');
            if (skillFilter) skillFilter.reset();
        }
    }

    search() {
        const skillFilter = this.filtersComponents?.find((c) => c.radioButtonGroupName === 'skillGroup');
        const interestFilter = this.filtersComponents?.find((c) => c.radioButtonGroupName === 'interestGroup');
        if (this.activeTabIndex === 0 && skillFilter) {
            if (skillFilter.selectedOption === 'mySkillsOrInterests' && this.skills.length === 0) {
                this.searchSubmit.emit(null);
                this.closeModal();
                return;
            }
        }

        if (this.activeTabIndex === 1 && interestFilter) {
            if (interestFilter.selectedOption === 'mySkillsOrInterests' && this.interests.length === 0) {
                this.searchSubmit.emit(null);
                this.closeModal();
                return;
            }
        }

        const filters = new Filters();

        if (this.activeTabIndex === 0 && skillFilter) {
            if (skillFilter.selectedOption === 'mySkillsOrInterests') {
                filters.skillsIds = this.skills.map((s) => s.id);
            } else if (skillFilter.selectedOption === 'specificSkillsOrInterest' && skillFilter.selectedItem) {
                filters.skillsIds = [skillFilter.selectedItem.id];
            }
        }

        if (this.activeTabIndex === 1 && interestFilter) {
            if (interestFilter.selectedOption === 'mySkillsOrInterests') {
                filters.interestsIds = this.interests.map((i) => i.id);
            } else if (interestFilter.selectedOption === 'specificSkillsOrInterest' && interestFilter.selectedItem) {
                filters.interestsIds = [interestFilter.selectedItem.id];
            }
        }

        if (this.selectedCommunity) {
            const community = new CommunityFilter(this.selectedCommunity.id, []);
            filters.communities = [community];
        }

        this.searchSubmit.emit(filters);
        this.closeModal();
    }

    private loadUserProfile() {
        this.profileService
            .getUserProfileById(this.accountsService.currentUser.userId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (user) => {
                    this.user = user;
                    this.skills = user.skills ?? [];
                    this.interests = user.interests ?? [];
                },
                error: (error) => {
                    this.toastrHelper.showGenericError(error);
                },
            });
    }

    public reset() {
        if (this.filtersComponents) {
            this.filtersComponents.forEach((component) => {
                component.reset();
            });
        }
        this.selectedCommunity = null;
        this.activeTabIndex = 0;
    }
}
